import React, { useState } from 'react';
import { 
    Container, 
    Typography, 
    Box, 
    Card, 
    CardContent, 
    Button, 
    Chip, 
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton
} from '@mui/material';
import { CalendarToday, AccessTime, Close, Videocam, Phone } from '@mui/icons-material';
import PageWrapper from '../components/PageWrapper';
import OpenHouseRegistrationForm from '../components/forms/OpenHouseRegistrationForm';

const OpenHouse = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);

    const handleRegisterClick = (event) => {
        setSelectedEvent(event);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedEvent(null);
    };

    const handleRegistrationSuccess = () => {
        setTimeout(() => {
            handleCloseModal();
        }, 3000);
    };


    return (
        <PageWrapper configKey="open-house">
            {(config) => (
                <>
                    <Container maxWidth="md" sx={{ mt: 3, mb: 3 }}>
                        <Typography variant="h3" component="h1" gutterBottom sx={{
                            textAlign: 'center',
                            mb: 4
                        }}>
                            Open House
                        </Typography>

                        {/* Introduction */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                                {config.description}
                            </Typography>

                            <Typography variant="body1" paragraph sx={{
                                fontSize: '1.1rem',
                                lineHeight: 1.7,
                                backgroundColor: 'rgba(0, 38, 255, 0.04)',
                                p: 3,
                                borderRadius: 2,
                                border: '3px solid',
                                borderColor: 'primary.100'
                            }}>
                                {config.sessionDescription}
                            </Typography>
                        </Box>

                        {/* Zoom Meeting */}
                        {config.zoomMeeting?.enabled && (
                            <Box sx={{
                                mb: 4,
                                p: { xs: 3, sm: 4 },
                                borderRadius: 3,
                                backgroundColor: 'primary.dark',
                                color: '#fff',
                                textAlign: 'center',
                                boxShadow: '0 8px 24px rgba(23, 37, 83, 0.3)',
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.5 }}>
                                    <Videocam sx={{ fontSize: '1.8rem' }} />
                                    <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                                        {config.zoomMeeting.title}
                                    </Typography>
                                </Box>
                                <Typography variant="body1" sx={{ mb: 2.5, opacity: 0.85 }}>
                                    {config.zoomMeeting.dateTime}
                                </Typography>

                                <Button
                                    variant="contained"
                                    color="secondary"
                                    size="large"
                                    href={config.zoomMeeting.meetingLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    startIcon={<Videocam />}
                                    sx={{
                                        px: 4,
                                        py: 1.5,
                                        fontSize: '1.05rem',
                                        fontWeight: 'bold',
                                        borderRadius: 2,
                                        mb: 2.5,
                                    }}
                                >
                                    Join Zoom Meeting
                                </Button>

                                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: { xs: 1, sm: 3 } }}>
                                    <Typography
                                        component="a"
                                        href={config.zoomMeeting.chatLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="body2"
                                        sx={{
                                            color: 'secondary.main',
                                            textDecoration: 'underline',
                                            textUnderlineOffset: '3px',
                                            '&:hover': { color: 'secondary.dark' }
                                        }}
                                    >
                                        Meeting Chat Link
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                        Meeting ID: {config.zoomMeeting.meetingId}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Open House Events */}
                        {config.events?.length > 0 ? (
                            <>
                                <Typography variant="h4" component="h2" gutterBottom sx={{
                                    mb: 3,
                                    color: 'primary.dark',
                                    textAlign: 'center'
                                }}>
                                    Choose Your Session
                                </Typography>
                                <Box sx={{ textAlign: 'center', mb: 4 }}>
                                    <Chip
                                        label={`All times listed in ${config.timeZone}`}
                                        color="primary"
                                        variant="outlined"
                                        icon={<AccessTime />}
                                    />
                                </Box>

                                <Grid container spacing={2} sx={{ mb: 4, justifyContent: 'center' }}>
                                    {config.events.map((event, index) => (
                                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                                            <Card
                                                elevation={0}
                                                sx={{
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                borderRadius: 3,
                                                overflow: 'hidden',
                                                border: '1px solid',
                                                borderColor: 'rgba(37, 61, 141, 0.14)',
                                                background: 'linear-gradient(165deg, #ffffff 0%, #f4f7fd 55%, #eaf0fb 100%)',
                                                boxShadow: '0 4px 18px rgba(37, 61, 141, 0.14), 0 1px 4px rgba(23, 37, 83, 0.08)',
                                                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    boxShadow: '0 12px 28px rgba(37, 61, 141, 0.22), 0 4px 8px rgba(23, 37, 83, 0.1)'
                                                }
                                            }}>
                                                <Box
                                                    sx={{
                                                        height: 5,
                                                        background: 'linear-gradient(90deg, #253D8D 0%, #F3B232 100%)',
                                                    }}
                                                />
                                                <CardContent sx={{
                                                    p: 2.5,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    textAlign: 'center',
                                                    flexGrow: 1
                                                }}>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        mb: 1.5,
                                                        color: 'primary.main'
                                                    }}>
                                                        <CalendarToday sx={{ fontSize: '1.3rem' }} />
                                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                            {event.date}
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        mb: 2.5,
                                                        color: 'text.secondary'
                                                    }}>
                                                        <AccessTime sx={{ fontSize: '1.1rem' }} />
                                                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                                            {event.time}
                                                        </Typography>
                                                    </Box>

                                                    <Button
                                                        variant="contained"
                                                        color="secondary"
                                                        size="medium"
                                                        onClick={() => handleRegisterClick(event)}
                                                        sx={{
                                                            px: 3,
                                                            py: 1,
                                                            fontSize: '0.9rem',
                                                            fontWeight: 'regular',
                                                            borderRadius: 2,
                                                            mt: 'auto',
                                                        }}
                                                    >
                                                        Register
                                                    </Button>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </>
                        ) : (
                            <Box
                                sx={{
                                    mb: 4,
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'rgba(37, 61, 141, 0.14)',
                                    background: 'linear-gradient(165deg, #ffffff 0%, #f4f7fd 55%, #eaf0fb 100%)',
                                    boxShadow: '0 4px 18px rgba(37, 61, 141, 0.14), 0 1px 4px rgba(23, 37, 83, 0.08)',
                                }}
                            >
                                <Box
                                    sx={{
                                        height: 5,
                                        background: 'linear-gradient(90deg, #253D8D 0%, #F3B232 100%)',
                                    }}
                                />
                                <Box sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                                    <Typography variant="h5" component="h2" sx={{
                                        color: 'primary.dark',
                                        fontWeight: 'bold',
                                        mb: 1.5
                                    }}>
                                        Call to Schedule an Appointment
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                        color: 'text.secondary',
                                        lineHeight: 1.7,
                                        maxWidth: 520,
                                        mx: 'auto'
                                    }}>
                                        Open Houses are scheduled throughout the year.<br/>Please call to schedule an appointment: <strong>433-393-0892</strong>
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Additional Information */}
                        {config.events?.length > 0 && (
                            <Box sx={{ mt: 5, p: 3, backgroundColor: 'grey.50', borderRadius: 2, boxShadow: 3 }}>
                                <Typography variant="h5" component="h3" gutterBottom sx={{
                                    color: 'primary.dark',
                                    textAlign: 'center'
                                }}>
                                    Can't Make These Times?
                                </Typography>
                                <Typography variant="body1" sx={{
                                    textAlign: 'center',
                                    lineHeight: 1.6
                                }}>
                                    No problem! Contact us at <strong>433-393-0892</strong> or {' '}
                               
                                    <Typography
                                        component="a"
                                        href={`mailto:${config.openHouseEmail}`}
                                        sx={{
                                            fontWeight: 'bold',
                                            color: 'primary.main',
                                            textDecoration: 'none',
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        }}
                                    >
                                        {config.openHouseEmail}
                                    </Typography>
                                    {' '}to schedule an appointment at a time that works for your family.
                                </Typography>
                            </Box>
                        )}
                    </Container>

                    {/* Registration Modal */}
                    <Dialog 
                        open={modalOpen} 
                        onClose={handleCloseModal}
                        maxWidth="sm"
                        fullWidth
                        PaperProps={{
                            sx: {
                                borderRadius: 3,
                                boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                            }
                        }}
                    >
                        <DialogTitle sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            pb: 1
                        }}>
                            <IconButton
                                onClick={handleCloseModal}
                                sx={{ 
                                    position: 'absolute',
                                    right: 8,
                                    top: 8
                                }}
                            >
                                <Close />
                            </IconButton>
                        </DialogTitle>
                        
                        <DialogContent sx={{ pt: 2 }}>
                            <OpenHouseRegistrationForm
                                selectedEvent={selectedEvent}
                                onCancel={handleCloseModal}
                                onSuccess={handleRegistrationSuccess}
                            />
                        </DialogContent>
                    </Dialog>
                </>
            )}
        </PageWrapper>
    );
};

export default OpenHouse; 